import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Pill, FileText, MessageCircle, LogOut, Heart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [appointments, setAppointments] = useState([]);
  const [refills, setRefills] = useState([]);
  const [regimens, setRegimens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsRes, refillsRes, regimensRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/appointments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/refills`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/regimens`, { withCredentials: true })
      ]);
      setAppointments(appointmentsRes.data);
      setRefills(refillsRes.data);
      setRegimens(regimensRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const pendingRefills = refills.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="patient-dashboard">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-accent" fill="currentColor" />
              <div>
                <h1 className="font-heading text-xl font-semibold text-text-primary\">Welcome Back, {user?.name?.split(' ')[0]}</h1>
                <p className="text-sm text-text-secondary font-body\">Your health dashboard</p>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-body">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <button
            data-testid="nav-appointments-btn"
            onClick={() => navigate('/appointments')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <Calendar className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Appointments</h3>
            <p className="text-text-secondary font-body text-sm">Schedule with your doctor</p>
            {pendingAppointments.length > 0 && (
              <span className="status-badge status-pending mt-2">({pendingAppointments.length} pending)</span>
            )}
          </button>

          <button
            data-testid="nav-refills-btn"
            onClick={() => navigate('/refills')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <Pill className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Refills</h3>
            <p className="text-text-secondary font-body text-sm">Request medication refills</p>
            {pendingRefills.length > 0 && (
              <span className="status-badge status-pending mt-2">({pendingRefills.length} pending)</span>
            )}
          </button>

          <button
            data-testid="nav-regimen-btn"
            onClick={() => navigate('/regimen')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <FileText className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">My Regimen</h3>
            <p className="text-text-secondary font-body text-sm">View treatment plan</p>
          </button>

          <button
            data-testid="nav-chat-btn"
            onClick={() => navigate('/chat')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <MessageCircle className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">AI Assistant</h3>
            <p className="text-text-secondary font-body text-sm">Ask health questions</p>
          </button>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Appointments */}
          <div className="card-default" data-testid="upcoming-appointments-section">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Your Appointments</h2>
            {appointments.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary font-body">No appointments yet</p>
                <button
                  data-testid="schedule-appointment-btn"
                  onClick={() => navigate('/appointments')}
                  className="mt-4 btn-primary"
                >
                  Schedule Appointment
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.slice(0, 5).map((appointment) => (
                  <div key={appointment.appointment_id} className="border border-stone-100 rounded-lg p-4" data-testid={`appointment-${appointment.appointment_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-body font-medium text-text-primary">{appointment.reason}</p>
                        <p className="text-sm text-text-secondary font-body mt-1">
                          {appointment.requested_date} at {appointment.requested_time}
                        </p>
                      </div>
                      <span className={`status-badge status-${appointment.status}`}>
                        {appointment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Refills */}
          <div className="card-default" data-testid="refills-section">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Refill Requests</h2>
            {refills.length === 0 ? (
              <div className="text-center py-8">
                <Pill className="w-12 h-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary font-body">No refill requests</p>
                <button
                  data-testid="request-refill-btn"
                  onClick={() => navigate('/refills')}
                  className="mt-4 btn-primary"
                >
                  Request Refill
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {refills.slice(0, 5).map((refill) => (
                  <div key={refill.refill_id} className="border border-stone-100 rounded-lg p-4" data-testid={`refill-${refill.refill_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-body font-medium text-text-primary">{refill.medication_name}</p>
                        <p className="text-sm text-text-secondary font-body mt-1">
                          {refill.dosage} - Qty: {refill.quantity}
                        </p>
                      </div>
                      <span className={`status-badge status-${refill.status}`}>
                        {refill.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Health Regimen */}
        {regimens.length > 0 && (
          <div className="card-default mt-6" data-testid="regimen-section">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Your Health Regimen</h2>
            <div className="space-y-4">
              {regimens.map((regimen) => (
                <div key={regimen.regimen_id} className="border border-stone-100 rounded-lg p-4">
                  <h3 className="font-body font-semibold text-text-primary mb-2">{regimen.title}</h3>
                  <p className="text-text-secondary font-body text-sm mb-3">{regimen.description}</p>
                  <button
                    onClick={() => navigate('/regimen')}
                    className="text-primary font-body text-sm hover:underline"
                  >
                    View Details →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}